// Context for managing title language preference (English/Romaji)

import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { waitForServerAuth } from '../services/authReadyService';

const TitlePreferenceContext = createContext();

const STORAGE_KEY = 'anime_name_preference';

export const TitlePreferenceProvider = ({ children }) => {
    // Initialize from localStorage, default to 'english'
    const [titlePreference, setTitlePreferenceState] = useState(() => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            return stored || 'english';
        } catch (error) {
            console.error('Error reading title preference from localStorage:', error);
            return 'english';
        }
    });

    // Track if this is the initial mount (skip server save for initial value)
    const isInitialMount = useRef(true);

    // Persist to localStorage and server whenever preference changes
    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, titlePreference);

            // Skip server save on initial mount (UserDataSynchronizer handles initial sync)
            if (isInitialMount.current) {
                isInitialMount.current = false;
                return;
            }

            // Also save to server for cross-browser persistence (only if logged in)
            const { hasToken } = require('../services/tokenStorage');
            if (!hasToken()) return; // Skip server save if not authenticated

            const saveToServer = async () => {
                try {
                    await waitForServerAuth(); // Wait for server login to complete
                    const apiService = (await import('../services/apiService')).default;
                    await apiService.saveSettingToServer('anime_name_preference', titlePreference);
                    console.log('📡 Title preference saved to server');
                } catch (error) {
                    console.error('Failed to save title preference to server:', error);
                }
            };
            saveToServer();
        } catch (error) {
            console.error('Error saving title preference to localStorage:', error);
        }
    }, [titlePreference]);

    const setTitlePreference = (preference) => {
        if (preference === 'english' || preference === 'romanji') {
            setTitlePreferenceState(preference);
        } else {
            console.warn(`Invalid title preference: ${preference}. Use 'english' or 'romanji'.`);
        }
    };

    return (
        <TitlePreferenceContext.Provider value={{ titlePreference, setTitlePreference }}>
            {children}
        </TitlePreferenceContext.Provider>
    );
};

export const useTitlePreference = () => {
    const context = useContext(TitlePreferenceContext);
    if (!context) {
        throw new Error('useTitlePreference must be used within TitlePreferenceProvider');
    }
    return context;
};
